import React from 'react';
import { useDataTable } from '../DataTable';
import { useUrlFilter } from '../core/_helpers';

const LABEL_SUFFIX = '_label';

/**
 * Renders the currently applied filters as removable chips below the toolbar
 * (ERPNext-style). A chip is shown for every filter key that carries a sibling
 * `<key>_label` value, so tables whose filters don't follow that convention
 * simply render nothing. Removing a chip clears both the context filter and the
 * URL state, keeping every filter source in sync.
 */
const AppliedFilterChips: React.FC = () => {
  const { filter, clearFilter, tableKey } = useDataTable();
  const [, setUrlFilter] = useUrlFilter<Record<string, unknown>>(tableKey);

  const chips = filter
    ? Object.keys(filter)
        .filter((key) => !key.endsWith(LABEL_SUFFIX) && `${key}${LABEL_SUFFIX}` in filter)
        .map((key) => ({ key, label: String(filter[`${key}${LABEL_SUFFIX}`] ?? '') }))
        .filter((chip) => chip.label.length > 0)
    : [];

  if (chips.length === 0) return null;

  const removeChip = (key: string) => {
    clearFilter(key);
    clearFilter(`${key}${LABEL_SUFFIX}`);
    setUrlFilter((prev) => {
      if (!prev) return null;
      const next = { ...prev };
      delete next[key];
      delete next[`${key}${LABEL_SUFFIX}`];
      return Object.keys(next).length > 0 ? next : null;
    });
  };

  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
      {chips.map(({ key, label }) => (
        <span
          key={key}
          className="badge badge-light-primary fw-bold border border-primary border-dashed d-inline-flex align-items-center"
        >
          {label}
          <button
            aria-label={`Remover filtro ${label}`}
            className="btn btn-icon btn-active-color-primary h-auto w-auto p-0 ms-2 text-primary"
            type="button"
            onClick={() => removeChip(key)}
          >
            <i className="bi bi-x fs-4" />
          </button>
        </span>
      ))}
    </div>
  );
};

export default AppliedFilterChips;
